import { type ClassValue, clsx } from "clsx";
import { addDays, set } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { twMerge } from "tailwind-merge";

import { FullName, UserProps } from "@/app/app.interface";
import {
  APP_LOCAL_KEYS,
  ONE_KB,
  ONE_MB,
  States,
} from "@/app/constants/constants";
import { getCurrentFortnight } from "@/components/filters/FortnightPicker";
import { GetLatestDocketPdfProps } from "@/features/docket/docket.interfaces";
import { Workspace } from "@/features/workspace/workspace.interfaces";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { typedLocalStorage } from "@/utils/storage/localStorage";

/**
 * @description it pads a number or string with zeros
 * @param {string | number | undefined | null} value - value to pad
 * @param {number} count - number of digits
 * @returns {string} - padded value
 */
export const zeroPadding = (
  value: string | number | undefined | null,
  count: number = 4,
): string => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return value.toString().padStart(count, "0");
};

/**
 * @param {...any} inputs - list of classes
 * @description this is cn to use tailwind classes
 * @returns {string} - returns the string of classes
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export const isAdminDomain = window.location.hostname.split(".")[0] === "admin";
export const isCustomerDomain =
  window.location.hostname.split(".")[0] === "customer";
export const isSubDomain = !isAdminDomain && !isCustomerDomain;

export const getRootUserId = (): string => {
  const userData = typedLocalStorage.getItem(
    APP_LOCAL_KEYS.USER_DATA,
  ) as UserProps | null;

  return userData?.id ?? "-";
};

/**
 * Get the workspace timezone from localStorage
 * @returns {string} The workspace timezone or fallback timezone
 */
const getWorkspaceTimezone = (): string => {
  try {
    const workspaceDetails = typedLocalStorage.getItem(
      APP_LOCAL_KEYS.WORKSPACE_DATA,
    ) as Workspace | null;

    return (
      workspaceDetails?.timezone ||
      States.find((state) => state?.name === workspaceDetails?.state)
        ?.timezone ||
      "America/New_York" // fallback timezone
    );
  } catch (error) {
    return "America/New_York"; // fallback timezone
  }
};

/**
 * @description it converts enum to object
 * @param {object} enumObj - enum object
 * @returns {{key: string, value: string}[]} - array of enum values
 */
export const enumToObj = (
  enumObj: object,
): {
  key: string;
  value: string;
}[] => {
  return Object.keys(enumObj).map((key) => ({
    key,
    value: enumObj[key as keyof typeof enumObj],
  }));
};

type ArrayOrObject<T> = T[] | Record<string, T[]> | null | undefined;
/**
 * @description it returns the display value of an array or object
 * @param {ArrayOrObject<T> } input - input array or object
 * @param {boolean} isUnique - flag to check if the values are unique
 * @returns {string} - display value
 */
export function getDisplayValue<T>(
  input: ArrayOrObject<T>,
  isUnique: boolean = false,
): string {
  if (!input) {
    return "";
  }

  let values: T[] = [];

  if (Array.isArray(input)) {
    values = input.filter(Boolean);
  } else {
    values = Object.values(input).flat().filter(Boolean);
  }

  if (values.length === 0) {
    return "";
  }

  if (isUnique && values.length > 1) {
    const uniqueValues = new Set(values);

    return uniqueValues.size > 1
      ? "Multiple"
      : (Array.from(uniqueValues)[0] as unknown as string);
  }

  return values.length > 1
    ? "Multiple"
    : ((values[0] as unknown as string) ?? "");
}

type Props = string | number | null | undefined;

/**
 * @description Extracts the data for sorting.
 * @param {Props} value - Input value
 * @returns {string | number | undefined} - Extracted value for sorting
 */
export const extractSortData = (value: Props): string | number | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  if (Number.isInteger(value)) {
    return Number(value);
  }

  if (typeof value === "string") {
    return value.toLowerCase();
  }

  return value;
};

/**
 * @description Converts the size in bytes to KB
 * @param {number} size - size in bytes
 * @returns {string} - size in KB
 */
export const formatSizeToKB = (size: number): string => {
  return `${(size / ONE_KB).toFixed(2)} KB`;
};

export const DateStringToZeroDate = (date: string): string => {
  const timezone = new Date().getTimezoneOffset();
  const formattedDate = new Date(date).setHours(0, 0, 0, 0);
  const zeroOffsetTime = new Date(
    formattedDate - timezone * 60 * 1000,
  ).toISOString();
  //if zero offset time is like this zeroOffsetTime 2025-03-30T23:00:00.000Z then add 1 day and convert it to 2025-03-31T00:00:00.000Z
  if (zeroOffsetTime.includes("T23:00:00.000Z")) {
    return addDays(
      zeroOffsetTime.replace("T23:00:00.000Z", "T00:00:00.000Z"),
      1,
    ).toISOString();
  } else if (zeroOffsetTime.includes("T01:00:00.000Z")) {
    return zeroOffsetTime.replace("T01:00:00.000Z", "T00:00:00.000Z");
  }

  return zeroOffsetTime;
};

export const formatToCapitalizedWords = (value: string): string => {
  if (!value) {
    return "";
  }

  if (value.includes("_")) {
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const separatedValue = value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

  return separatedValue
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const countWordsLength = (str: string): number => {
  if (!str || str.trim().length === 0) {
    return 0;
  }

  // Remove extra whitespace and split into words
  const words = str
    .trim()
    // Replace multiple spaces with single space
    .replace(/\s+/g, " ")
    // Replace common punctuation with spaces
    .replace(/[.,!?;:()[\]{}""''`]/g, " ")
    // Split on remaining whitespace
    .split(/\s+/);

  return words.length;
};

export interface RenderFullNameProps {
  userData?: Partial<UserProps> | null;
  params?: FullName | null;
}
/**
 * Renders the full name of a user by concatenating their first, middle, and last names.
 *
 * If the `params` object is provided, it takes precedence over `userData` for constructing the name.
 * Any undefined or empty parts (firstName, middleName, or lastName) will be ignored in the final output.
 * @param {object} options - The input options.
 * @param {object} [options.userData] - The user data containing name fields.
 * @param {string} [options.userData.firstName] - The user's first name.
 * @param {string} [options.userData.middleName] - The user's middle name.
 * @param {string} [options.userData.lastName] - The user's last name.
 * @param {object} [options.params] - The name parameters to override `userData`.
 * @param {string} [options.params.firstName] - The override for the user's first name.
 * @param {string} [options.params.middleName] - The override for the user's middle name.
 * @param {string} [options.params.lastName] - The override for the user's last name.
 * @returns {string} The concatenated full name, or an empty string if no valid name parts are provided.
 */

export const renderFullName = ({
  userData = null,
  params,
}: RenderFullNameProps): string => {
  // If both `userData` and `params` are null/undefined, return an empty string.
  if (!userData && !params) {
    return "";
  }

  // Use `params` if provided, overriding `userData`.
  if (params) {
    const { firstName = "", middleName = "", lastName = "" } = params;
    const fullName = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(" ");

    return fullName || ""; // Ensure an empty string if all parts are blank.
  }

  // Fallback to `userData` if `params` is not provided.
  if (userData) {
    const { firstName = "", middleName = "", lastName = "" } = userData;
    const fullName = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(" ");

    return fullName || ""; // Ensure an empty string if all parts are blank.
  }

  // Default to an empty string (should never reach here due to earlier checks).
  return "";
};

interface FormatWithPadStartProps {
  value: string | number;
  prefix?: string;
  length: number;
  padChar?: string;
}

/**
 * Adds a prefix and pads the input value to a specified length.
 * @param {string | number} value - The value to be padded.
 * @returns {string} A formatted string with the prefix and padded value.
 * @example formatWithPadStart(123, "INV-", 6); // Returns "INV-000123"
 * @example formatWithPadStart("ABC", "ID-", 5, "-"); // Returns "ID--ABC"
 */
export function formatWithPadStart({
  value,
  prefix = "0",
  length,
  padChar = "0",
}: FormatWithPadStartProps): string {
  const stringValue = String(value); // Convert the input to a string
  const paddedValue = stringValue.padStart(length, padChar); // Pad the value

  return `${prefix}${paddedValue}`; // Combine prefix and padded value
}

//Function to filter columns based on key
export const filterColumns = <T>({
  columns,
  hiddenColumnsHeaders,
}: {
  columns: ExtendedColumnDef<T>[];
  hiddenColumnsHeaders: string[];
}): ExtendedColumnDef<T>[] =>
  hiddenColumnsHeaders.length === 0
    ? columns
    : columns.filter(
        (column) =>
          !column.header ||
          !hiddenColumnsHeaders.includes(column.header as string),
      );

/**
 * Generates a query string for the current fortnight's start and end dates (timezone-aware).
 * @returns {string} - Query string in the format `startDate=<startDate>&endDate=<endDate>`
 */
export const getCurrentFortnightQuery = (): string => {
  // Get the current fortnight
  const currentFortnightdate = getCurrentFortnight();
  const timezone = getWorkspaceTimezone();

  try {
    // Convert dates to user's timezone before formatting
    const startDateInTimezone = toZonedTime(
      currentFortnightdate.start,
      timezone,
    );
    const endDateInTimezone = toZonedTime(currentFortnightdate.end, timezone);

    // Format the start and end dates in the user's timezone
    const startDate = set(startDateInTimezone, {
      hours: 0,
      minutes: 0,
      seconds: 0,
    }).toISOString();

    const endDate = set(endDateInTimezone, {
      hours: 23,
      minutes: 59,
      seconds: 59,
    }).toISOString();

    // Return the query string
    return `startDate=${DateStringToZeroDate(startDate)}&endDate=${DateStringToZeroDate(endDate)}`;
  } catch (error) {
    // Fallback to original logic if timezone conversion fails
    const startDate = set(new Date(currentFortnightdate.start), {
      hours: 0,
      minutes: 0,
      seconds: 0,
    }).toISOString();

    const endDate = set(new Date(currentFortnightdate.end), {
      hours: 23,
      minutes: 59,
      seconds: 59,
    }).toISOString();

    return `startDate=${DateStringToZeroDate(startDate)}&endDate=${DateStringToZeroDate(endDate)}`;
  }
};

/**
 * Formats a file size in bytes into a human-readable string representation.
 * @param {number} sizeInBytes - The size of the file in bytes.
 * @param {boolean} isFolder - A boolean indicating whether the item is a folder.
 *                   If true, the function will return a placeholder ("-").
 * @returns {string} A string representing the file size in either KB or MB.
 *          If the size is 0 bytes, it returns "0 KB".
 *          If the size is 1 MB or greater, it is formatted in MB with two decimal places.
 *          Otherwise, it is formatted in KB with two decimal places.
 */
export const formatFileSize = (
  sizeInBytes: number,
  isFolder: boolean,
): string => {
  if (isFolder) {
    return "-";
  }
  if (sizeInBytes === 0) {
    return "0 KB";
  }

  const sizeInMb = sizeInBytes / ONE_MB;
  const sizeInKb = sizeInBytes / ONE_KB;

  return sizeInMb >= 1
    ? `${sizeInMb.toFixed(2)} MB`
    : `${sizeInKb.toFixed(2)} KB`;
};

/**
 * Recursively replaces all undefined values in an object with null.
 * This is useful for preparing data for JSON serialization, as undefined values
 * are omitted during serialization while null values are preserved.
 * @template T - The type of the input object
 * @param {T} obj - The object to transform
 * @returns {T} A new object with all undefined values replaced by null
 * @example
 * const data = { a: 1, b: undefined, c: { d: undefined } };
 * const sanitized = replaceUndefinedWithNull(data);
 * // Returns { a: 1, b: null, c: { d: null } }
 */
export function replaceUndefinedWithNull<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(replaceUndefinedWithNull) as unknown as T;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

export const removeHyphens = (str: string): string | null => {
  if (!str) {
    return null;
  }

  return str.replace(/-/g, "");
};

export type Currency = "USD" | "AUD" | "NPR";
export const getCurrencySymbol = (currency: Currency): string => {
  const currencySymbols: Record<Currency, string> = {
    USD: "$",
    AUD: "A$",
    NPR: "Rs ",
  };

  return currencySymbols[currency];
};

export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (a === null || b === null) {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, index) => isDeepEqual(item, b[index]));
  }

  if (typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a as Record<string, unknown>);
    const bKeys = Object.keys(b as Record<string, unknown>);

    if (aKeys.length !== bKeys.length) {
      return false;
    }

    return aKeys.every((key) =>
      isDeepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
      ),
    );
  }

  return false;
};

export const getChangedValues = <T extends Record<string, unknown>>(
  currentValues: T,
  initialValues: T,
): Partial<T> => {
  return Object.keys(currentValues).reduce<Partial<T>>((acc, key) => {
    if (!isDeepEqual(currentValues[key], initialValues[key])) {
      (acc as Record<string, unknown>)[key] = currentValues[key];
    }

    return acc;
  }, {});
};

export const ensureMutationSuccess = async <T>(
  mutationFn: () => Promise<MutationResponse>,
): Promise<T> => {
  const result = await mutationFn();

  if ("error" in result && result.error) {
    throw new Error(
      typeof result.error === "object" && "message" in result.error
        ? result.error.message
        : "Something went wrong",
    );
  }

  if (!("data" in result) || !result.data) {
    throw new Error("No data received");
  }

  return result.data as T;
};

export const generateYearRange = (back: number, forward: number): number[] => {
  const currentYear = new Date().getFullYear();

  if (back < 0 || forward < 0) {
    throw new Error("generateYearRange: back and forward must be non-negative");
  }

  return Array.from(
    { length: back + forward + 1 },
    (_, i) => currentYear - back + i,
  );
};

/**
 * Gets the latest PDF URL from a docket's history or falls back to the main docketPdf.
 * @param {GetLatestDocketPdfProps} docket - The docket object containing pdf and pdf history
 * @param {string|number} [version] - Optional version to match
 * @returns {string} The latest or version-specific PDF URL string
 */
export const getLatestDocketPdf = (
  docket: GetLatestDocketPdfProps,
  version?: number | string,
): string => {
  if (!docket.docketPdfHistory?.length) {
    return docket.docketPdf;
  }

  // Sort history by createdAt descending to get the latest
  const sortedHistory = [...docket.docketPdfHistory].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  if (version != null) {
    const versionMatch = sortedHistory.find(
      (h) => h.version?.toString() === version.toString(),
    );
    if (versionMatch?.url) {
      return versionMatch.url;
    }
  }

  return sortedHistory[0]?.url || docket.docketPdf;
};
